import { apiFetch } from '../utils/apiClient';
import { refreshMe } from '../hooks/useMe';

/**
 * Typed client for every `/settings/*` route (super-admin only). Phase 7's
 * Settings UI consumes this module; the endpoints are laid down here in
 * Phase 6 so the wire is ready.
 *
 * Any mutation that could change the caller's own profile or memberships
 * calls `refreshMe()` so the shared `/me` cache picks up the new state on
 * the next render.
 */

/* ---------- Users ---------- */

export interface SettingsUser {
  userId: string;
  displayName: string;
  handles: string[];
  knownAddresses: string[];
  color: string;
  disabled?: boolean;
  /** Team-ids of company groups this user is currently a member of. */
  companies: string[];
  /** Other users (subs) who can see this user's personal partition. */
  personalSharedWith: string[];
}

export interface InviteUserInput {
  email: string;
  displayName: string;
  handles?: string[];
  knownAddresses?: string[];
  color?: string;
  companyIds?: string[];
  personalSharedWith?: string[];
}

export async function listUsers(
  includeDisabled = false
): Promise<SettingsUser[]> {
  const qs = includeDisabled ? '?includeDisabled=1' : '';
  const { users } = await apiFetch<{ users: SettingsUser[] }>(
    `/settings/users${qs}`
  );
  return users;
}

export async function inviteUser(input: InviteUserInput): Promise<{ sub: string }> {
  const result = await apiFetch<{ sub: string }>('/settings/users', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  // An invite can add the new user to groups the caller is ALSO a member of
  // (the `personalSharedWith` list can reference the caller). Refresh to
  // pick up the new membership edges on the visible-groups side.
  await refreshMe().catch(() => {});
  return result;
}

export interface UpdateUserInput {
  displayName?: string;
  handles?: string[];
  knownAddresses?: string[];
  color?: string;
  disabled?: boolean;
  personalSharedWith?: string[];
  /** Replace-semantics: backend diffs and transact-writes the delta. */
  companyIds?: string[];
}

export async function updateUser(
  sub: string,
  patch: UpdateUserInput
): Promise<SettingsUser> {
  const { user } = await apiFetch<{ user: SettingsUser }>(
    `/settings/users/${encodeURIComponent(sub)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }
  );
  // If the caller updated their own profile, or toggled someone else's
  // `personalSharedWith` (which could add/remove the caller from a
  // personal group), refresh /me. Conservative: always refresh.
  await refreshMe().catch(() => {});
  return user;
}

export async function disableUser(sub: string): Promise<void> {
  await apiFetch<void>(`/settings/users/${encodeURIComponent(sub)}`, {
    method: 'DELETE',
  });
  await refreshMe().catch(() => {});
}

/* ---------- Companies ---------- */

export interface SettingsCompany {
  teamId: string;
  displayName: string;
  color: string;
  kind: 'company' | 'personal';
  memberCount?: number;
}

export interface CreateCompanyInput {
  displayName: string;
  color?: string;
  id?: string;
}

export async function listCompanies(): Promise<SettingsCompany[]> {
  const { companies } = await apiFetch<{ companies: SettingsCompany[] }>(
    '/settings/companies'
  );
  return companies;
}

export async function createCompany(
  input: CreateCompanyInput
): Promise<SettingsCompany> {
  const { company } = await apiFetch<{ company: SettingsCompany }>(
    '/settings/companies',
    {
      method: 'POST',
      body: JSON.stringify(input),
    }
  );
  // New company may not involve the caller, but super-admins see all company
  // groups via isSuperAdmin-aware logic — refresh anyway to stay consistent.
  await refreshMe().catch(() => {});
  return company;
}

export async function updateCompany(
  teamId: string,
  patch: { displayName?: string; color?: string }
): Promise<SettingsCompany> {
  const { company } = await apiFetch<{ company: SettingsCompany }>(
    `/settings/companies/${encodeURIComponent(teamId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }
  );
  await refreshMe().catch(() => {});
  return company;
}

export async function deleteCompany(teamId: string): Promise<void> {
  await apiFetch<void>(
    `/settings/companies/${encodeURIComponent(teamId)}`,
    { method: 'DELETE' }
  );
  await refreshMe().catch(() => {});
}

export async function addCompanyMember(
  teamId: string,
  sub: string
): Promise<void> {
  await apiFetch<void>(
    `/settings/companies/${encodeURIComponent(teamId)}/members`,
    {
      method: 'POST',
      body: JSON.stringify({ sub }),
    }
  );
  // If the caller is being added to this group (sub === caller's sub),
  // they now have a new visible group; refresh /me.
  await refreshMe().catch(() => {});
}

export async function removeCompanyMember(
  teamId: string,
  sub: string
): Promise<void> {
  await apiFetch<void>(
    `/settings/companies/${encodeURIComponent(teamId)}/members/${encodeURIComponent(sub)}`,
    { method: 'DELETE' }
  );
  await refreshMe().catch(() => {});
}
